import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../AuthContext';
import api from '../api';
import { applyLinkBranding, brandingIconUrl } from '../utils/linkBranding';

function MasterLinkBrandingSettings() {
  const [title, setTitle] = useState('');
  const [faviconUrl, setFaviconUrl] = useState('/nashicone.png');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [removeIcon, setRemoveIcon] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [reload, setReload] = useState(0);
  const fileInput = useRef(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    api.get('/link-branding').then(({ data }) => {
      if (!active) return;
      setTitle(data.title);
      setFaviconUrl(data.faviconUrl);
      setLoaded(true);
    }).catch(() => {
      if (active) setError('Não foi possível carregar a identidade dos links. Tente novamente.');
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [reload]);

  useEffect(() => {
    setPreview('');
    if (!file) return undefined;
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(file);
    return () => { reader.onload = null; if (reader.readyState === 1) reader.abort(); };
  }, [file]);

  const upload = event => {
    setSuccess('');
    setError('');
    const selected = event.target.files?.[0];
    if (!selected) return;
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(selected.type) || selected.size > 2 * 1024 * 1024) {
      setError('Envie uma imagem PNG, JPEG ou WebP de até 2 MB.');
      event.target.value = '';
      return;
    }
    setFile(selected);
    setRemoveIcon(false);
  };
  const restore = () => {
    setTitle('Nash iBPM');
    setFile(null);
    setRemoveIcon(true);
    setError('');
    setSuccess('');
    if (fileInput.current) fileInput.current.value = '';
  };
  const save = async event => {
    event.preventDefault();
    if (!loaded || saving) return;
    setSaving(true);
    setError('');
    setSuccess('');
    const payload = new FormData();
    payload.append('title', title);
    payload.append('removeIcon', String(removeIcon));
    if (file) payload.append('icon', file);
    try {
      const { data } = await api.put('/link-branding', payload);
      setTitle(data.title);
      setFaviconUrl(data.faviconUrl);
      setFile(null);
      setRemoveIcon(false);
      if (fileInput.current) fileInput.current.value = '';
      applyLinkBranding(data);
      setSuccess('Identidade dos links salva.');
    } catch (err) {
      setError(err.response?.data?.error || 'Não foi possível salvar a identidade dos links. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };
  const disabled = loading || saving || !loaded;
  return (
    <section className="rounded-lg bg-white p-5 shadow" aria-labelledby="link-branding-heading">
      <h2 id="link-branding-heading" className="text-lg font-semibold text-gray-900">Identidade dos links</h2>
      <p className="mt-1 text-sm text-gray-500">Defina o ícone e o título padrão desta instalação nas abas do navegador e nos links compartilhados. Configuração exclusiva do usuário master.</p>
      {loading && <p className="mt-3 text-sm text-gray-500">Carregando identidade dos links...</p>}
      <form onSubmit={save} className="mt-4 space-y-4">
        <fieldset disabled={disabled} className="space-y-4">
          <label className="block text-sm font-medium text-gray-700">Título padrão dos links
            <input type="text" maxLength={120} value={title} onChange={event => { setTitle(event.target.value); setSuccess(''); }} className="mt-1 w-full rounded border px-3 py-2" placeholder="Nome da sua instituição" />
          </label>
          <label className="block text-sm font-medium text-gray-700">Enviar favicon
            <input ref={fileInput} type="file" accept="image/png,image/jpeg,image/webp" onChange={upload} className="mt-1 block w-full text-sm" />
          </label>
          <p className="text-xs text-gray-500">PNG, JPEG ou WebP, até 2 MB. Prefira uma imagem quadrada. O ícone será ajustado automaticamente.</p>
          <div className="flex items-center gap-3 rounded-lg border bg-gray-50 p-3" aria-label="Prévia da identidade dos links">
            <img src={preview || brandingIconUrl(removeIcon ? '/nashicone.png' : faviconUrl)} alt="Prévia do favicon" className="h-10 w-10 rounded object-contain" />
            <span className="break-words font-medium text-gray-800">{title.trim() || 'Nash iBPM'}</span>
          </div>
          <div className="flex flex-wrap gap-3">
            <button type="submit" className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">{saving ? 'Salvando...' : 'Salvar identidade dos links'}</button>
            <button type="button" onClick={restore} className="rounded-lg border px-4 py-2 text-sm text-gray-700">Restaurar padrão Nash</button>
          </div>
        </fieldset>
        {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
        {!loading && !loaded && <button type="button" onClick={() => setReload(value => value + 1)} className="text-sm text-blue-700 underline">Tentar carregar novamente</button>}
        {success && <p role="status" className="text-sm text-green-700">{success}</p>}
        <p className="text-xs text-gray-500">Aplicativos de mensagens podem manter em cache a prévia de links já enviados e levar algum tempo para exibir a atualização.</p>
      </form>
    </section>
  );
}

export default function LinkBrandingSettings() {
  const user = useAuth()?.user;
  return user?.isMaster ? <MasterLinkBrandingSettings /> : null;
}
